import {
  getOpenTableOrders,
  getOpenOrderByOrderId
} from './androidOfflineCart';


const mapOfflineCheckoutItem = (
  item,
  localOrderId,
  index
) => {
  const unitPrice = Number(
    item?.unitPrice ??
    item?.per_item_price ??
    item?.price ??
    0
  );

  const quantity = Number(
    item?.quantity ??
    item?.item_quantity ??
    1
  );

  const totalPrice = Number(
    item?.totalPrice ??
    item?.total_price ??
    unitPrice * quantity
  );

  const uniqueCartId =
    item?.cart_id ||
    item?.localItemId ||
    `offline_${localOrderId}_${index}`;

  const productId = String(
    item?.productId ??
    item?.item_id ??
    item?.id ??
    ''
  );

  /*
   * productDetailJson can be:
   * 1. JSON string
   * 2. Already parsed object
   * 3. null
   */
  let productDetail = {};

  try {
    if (typeof item?.productDetailJson === 'string') {
      productDetail = item.productDetailJson.trim()
        ? JSON.parse(item.productDetailJson)
        : {};
    } else if (
      item?.productDetailJson &&
      typeof item.productDetailJson === 'object'
    ) {
      productDetail = item.productDetailJson;
    }
  } catch (error) {
    console.warn(
      '[OFFLINE_CHECKOUT] Failed parsing productDetailJson:',
      item?.productDetailJson,
      error
    );
  }

  /*
   * NAME
   */
  const itemName =
    item?.product_name ||
    item?.item_name ||
    item?.name ||
    item?.itemname ||
    productDetail?.name ||
    productDetail?.product_name ||
    '';

  /*
   * IMAGE
   */
  const image =
    item?.product_thumbnail ||
    item?.first_image_url ||
    item?.image ||
    productDetail?.image ||
    productDetail?.product_thumbnail ||
    '';

  /*
   * SIZE
   */
  const size =
    item?.product_size ||
    item?.selected_size ||
    item?.sizeName ||
    productDetail?.size_name ||
    '';

  /*
   * PRODUCT DETAILS
   */
  const productDetailText =
    item?.product_detail ||
    item?.product_detail_text ||
    productDetail?.product_detail ||
    productDetail?.description ||
    '';

  const mappedItem = {
    ...item,

    /*
     * IDs
     */
    cart_id: uniqueCartId,

    localItemId:
      item?.localItemId ||
      uniqueCartId,

    localOrderId:
      item?.localOrderId ||
      localOrderId ||
      '',

    item_id: productId,
    productId,

    /*
     * Fields expected by checkout UI
     */
    product_name: itemName,
    product_thumbnail: image,
    product_size: size,
    product_detail: productDetailText,

    /*
     * Keep alternate names too
     */
    item_name: itemName,
    name: itemName,

    /*
     * Quantity
     */
    item_quantity: quantity,
    quantity,

    /*
     * Price
     */
    per_item_price: unitPrice,
    unitPrice,

    total_price: totalPrice,
    totalPrice,

    /*
     * Size
     */
    selected_size: size,
    sizeName: size,
    sizeId: item?.sizeId || null,

    /*
     * Offline flags
     */
    is_previously_added: true,
    isPreviouslyAdded: true
  };

  console.log(
    '[OFFLINE_CHECKOUT] MAPPED ITEM:',
    JSON.stringify(mappedItem, null, 2)
  );

  return mappedItem;
};


/*
 * DINE-IN
 * Gets multiple open orders for a table.
 */
const getOfflineTableCheckout = async (tableId) => {
  if (!tableId) {
    return {
      items: [],
      data_deals: [],
      data_half_and_half: [],
      order_name: ''
    };
  }

  try {
    console.log(
      '[OFFLINE_CHECKOUT] Getting table orders:',
      tableId
    );

    const response =
      await getOpenTableOrders(tableId);

    console.log(
      '[OFFLINE_CHECKOUT] ===== RAW ANDROID TABLE RESPONSE =====',
      JSON.stringify(response, null, 2)
    );

    if (
      !response ||
      response.success !== true
    ) {
      console.warn(
        '[OFFLINE_CHECKOUT] Invalid table response:',
        response
      );

      return {
        items: [],
        data_deals: [],
        data_half_and_half: [],
        order_name: ''
      };
    }

    const orders = Array.isArray(response.orders)
      ? response.orders
      : [];

    const items = [];

    orders.forEach(
      (order, orderIndex) => {

        /*
         * The isCart order represents the
         * current session cart.
         *
         * Checkout should only display
         * previously saved orders here.
         */
        if (order?.isCart) {
          return;
        }

        const orderItems =
          Array.isArray(order?.items)
            ? order.items
            : [];

        orderItems.forEach(
          (item, itemIndex) => {

            const mappedItem =
              mapOfflineCheckoutItem(
                item,
                order?.localOrderId ||
                  `table_${tableId}_${orderIndex}`,
                itemIndex
              );

            items.push(mappedItem);
          }
        );
      }
    );

    const result = {
      items,
      data_deals: [],
      data_half_and_half: [],
      order_name: ''
    };

    console.log(
      '[OFFLINE_CHECKOUT] ===== FINAL TABLE CHECKOUT =====',
      JSON.stringify(result, null, 2)
    );

    return result;

  } catch (error) {

    console.error(
      '[OFFLINE_CHECKOUT] Failed table checkout:',
      error
    );

    return {
      items: [],
      data_deals: [],
      data_half_and_half: [],
      order_name: ''
    };
  }
};


/*
 * TAKEAWAY
 * Gets one specific offline order.
 */
const getOfflineOrderCheckout = async (
  localOrderId
) => {

  if (!localOrderId) {
    console.log(
      '[OFFLINE_CHECKOUT] No localOrderId'
    );

    return {
      items: [],
      data_deals: [],
      data_half_and_half: [],
      order_name: ''
    };
  }

  try {

    console.log(
      '[OFFLINE_CHECKOUT] Getting order:',
      localOrderId
    );

    const response =
      await getOpenOrderByOrderId(
        localOrderId
      );

    console.log(
      '[OFFLINE_CHECKOUT] ===== RAW ANDROID ORDER RESPONSE =====',
      JSON.stringify(response, null, 2)
    );

    console.log(
      '[OFFLINE_CHECKOUT] RAW ORDER ITEMS =====',
      JSON.stringify(
        response?.order?.items || [],
        null,
        2
      )
    );

    if (
      !response ||
      response.success !== true
    ) {
      console.warn(
        '[OFFLINE_CHECKOUT] Invalid order response:',
        response
      );

      return {
        items: [],
        data_deals: [],
        data_half_and_half: [],
        order_name: ''
      };
    }

    const order =
      response.order;

    const rawItems =
      Array.isArray(order?.items)
        ? order.items
        : [];

    const items =
      rawItems.map(
        (item, index) =>
          mapOfflineCheckoutItem(
            item,
            localOrderId,
            index
          )
      );

    const result = {
      items,
      data_deals: [],
      data_half_and_half: [],
      order_name:
        order?.orderName ||
        order?.order_name ||
        ''
    };

    console.log(
      '[OFFLINE_CHECKOUT] ===== FINAL ORDER CHECKOUT =====',
      JSON.stringify(result, null, 2)
    );

    return result;

  } catch (error) {

    console.error(
      '[OFFLINE_CHECKOUT] Failed getting order:',
      error
    );

    return {
      items: [],
      data_deals: [],
      data_half_and_half: [],
      order_name: ''
    };
  }
};

const createEmptyOfflineSummary = () => ({
  gross_total: 0,
  delivery_fee: 0,
  delivery_distance: 0,
  offer_discount: 0,
  offer_name: "",
  manual_discount: 0,
  manual_discount_type: null,
  manual_discount_value: null,

  split_payment: null,
  split_type: null,

  order_number: [],
  customer_name: "",

  cart_items: [[]],

  global_surcharges: [],
  gratuity_surcharges: [],
  applied_gratuity_id: null,

  loyalty_discount: 0,

  net_total: 0,

  order_type: "",
  order_id: null,

  windcave: [],
  linkly: [],

  table: null,

  pos_billing_print_prompt: false,
  pos_digital_receipt_enabled: true
});


const getOfflineCheckoutSummary = async ({
  tableId,
  localOrderId
}) => {

  console.log(
    '[OFFLINE_SUMMARY] ===== FUNCTION ENTERED ====='
  );

  console.log(
    '[OFFLINE_SUMMARY] tableId:',
    tableId
  );

  console.log(
    '[OFFLINE_SUMMARY] localOrderId:',
    localOrderId
  );

  let checkout;

  try {

    if (tableId) {

      console.log(
        '[OFFLINE_SUMMARY] Loading TABLE checkout...',
        tableId
      );

      checkout =
        await getOfflineTableCheckout(tableId);

    } else if (localOrderId) {

      console.log(
        '[OFFLINE_SUMMARY] Loading ORDER checkout...',
        localOrderId
      );

      checkout =
        await getOfflineOrderCheckout(localOrderId);

    } else {

      console.warn(
        '[OFFLINE_SUMMARY] No tableId or localOrderId!'
      );

      const emptySummary =
        createEmptyOfflineSummary();

      console.log(
        '[OFFLINE_SUMMARY] Returning EMPTY summary:',
        JSON.stringify(
          emptySummary,
          null,
          2
        )
      );

      return emptySummary;
    }

    console.log(
      '[OFFLINE_SUMMARY] ===== CHECKOUT RETURNED =====',
      JSON.stringify(
        checkout,
        null,
        2
      )
    );

    const items =
      checkout?.items || [];

    console.log(
      '[OFFLINE_SUMMARY] ITEMS:',
      JSON.stringify(
        items,
        null,
        2
      )
    );

    const grossTotal =
      items.reduce(
        (sum, item) => {

          const itemTotal =
            Number(
              item?.total_price || 0
            );

          console.log(
            '[OFFLINE_SUMMARY] Item total:',
            {
              name:
                item?.product_name ||
                item?.item_name ||
                item?.name,
              total: itemTotal
            }
          );

          return sum + itemTotal;
        },
        0
      );

    console.log(
      '[OFFLINE_SUMMARY] CALCULATED GROSS TOTAL:',
      grossTotal
    );

    const summary = {
      gross_total: grossTotal,

      delivery_fee: 0,
      delivery_distance: 0,

      offer_discount: 0,
      offer_name: "",

      manual_discount: 0,
      manual_discount_type: null,
      manual_discount_value: null,

      split_payment: null,
      split_type: null,

      cart_items: [items],

      global_surcharges: [],
      gratuity_surcharges: [],

      loyalty_discount: 0,

      net_total: grossTotal,

      order_type:
        tableId ? "3" : "1",

      order_id:
        localOrderId || null,

      order_number: [],

      customer_name: "",

      table:
        tableId
          ? {
              name: String(tableId),
              is_pager: false
            }
          : null,

      windcave: [],
      linkly: [],

      pos_billing_print_prompt: false,
      pos_digital_receipt_enabled: true
    };

    console.log(
      '[OFFLINE_SUMMARY] ===== FINAL SUMMARY =====',
      JSON.stringify(
        summary,
        null,
        2
      )
    );

    return summary;

  } catch (error) {

    console.error(
      '[OFFLINE_SUMMARY] ===== FAILED =====',
      error
    );

    const emptySummary =
      createEmptyOfflineSummary();

    console.log(
      '[OFFLINE_SUMMARY] Returning EMPTY after error:',
      JSON.stringify(
        emptySummary,
        null,
        2
      )
    );

    return emptySummary;
  }
};


export {
  getOfflineTableCheckout,
  getOfflineOrderCheckout,
  getOfflineCheckoutSummary
};
